import React from "react";
import { scrollToFirstError } from "@wtc/ui";
import { FormModal } from "@/components/ui/form-modal";
import { ClientAccessGrantsSummary } from "@/components/admin/access/ClientAccessGrantsSummary";
import { ClientSelectWithModal } from "@/components/ui/selects";
import { UserSelect } from "@/components/shared/UserSelect";
import { Button } from "@/components/ui/button";
import { AccessLevelInfoPopover } from "@/components/admin/access/AccessLevelInfoPopover";
import { ContactUserAccess } from "@/lib/types/database";
import type { User } from "@/lib/types/user";
import {
  accessLevelDescription,
  accessLevels,
  toAccessLevel,
} from "./grant-client-access/schema";
import { useGrantContactAccessForm } from "./grant-client-access/useGrantContactAccessForm";
import { GrantScopePreview } from "./grant-client-access/GrantScopePreview";

interface GrantContactAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The app_user being granted access. This is the AUTHORITATIVE id and the
   * preferred source — it comes straight from the modal-stack payload's
   * required `userId` field (see UserGrantClientAccessModalData).
   */
  userId?: string;
  /** Display data for the granted user (name in the read-only header). */
  user?: User;
  onSuccess?: () => void;
  access?: ContactUserAccess | null;
}

export const GrantContactAccessModal: React.FC<GrantContactAccessModalProps> = ({
  isOpen,
  onClose,
  userId,
  user,
  onSuccess,
  access,
}) => {
  const {
    form,
    formManager,
    effectiveUserId,
    hasPreselectedUser,
    pickedUserId,
    setPickedUserId,
    searchTerm,
    setSearchTerm,
    isSearching,
    initialClientsLoading,
    clientOptions,
    grantAccessMutation,
    revokeAccessMutation,
    revokeCompanyMutation,
    existingContactGrants,
    existingCompanyGrants,
    existingLoading,
    selectedAccessLevel,
    grantPreviewText,
    handleRevokeContact,
    handleRevokeCompany,
    submitGrant,
  } = useGrantContactAccessForm({ isOpen, userId, user, access, onSuccess, onClose });

  const handleSubmit = form.handleSubmit(submitGrant, (errors) => {
    scrollToFirstError(errors);
  });

  // ClientSelectWithModal is React.memo'd, so a fresh arrow every render would
  // defeat that memo. (validate:memo-stable-props cannot see this: it skips any
  // file whose memo is written with a type argument — React.memo<Props>( — so
  // this component is absent from its memo'd-name set entirely.)
  const handleContactChange = React.useCallback(
    (val: string) => {
      form.setValue("contact_id", val, { shouldValidate: true, shouldDirty: true });
    },
    [form],
  );

  // NOTE: contact-list/search loading must NOT gate the submit button — it only
  // affects the picker's options. Gating "Grant Access" on it made the button
  // permanently greyed out while those queries loaded/retried. Only the grant
  // mutation's own pending state disables submit.
  //
  // TYPING_FOCUS_STABILITY: this flag must likewise never reach a text input's
  // `disabled`. `isSearching` flips while the admin is mid-type, and disabling a
  // focused field blurs it and drops characters. It gates the picker's OPTIONS
  // (and, in edit mode, the whole control) — not the search box.
  const optionsLoading = initialClientsLoading || isSearching;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${access ? "Edit" : "Grant"} Contact Access${user ? ` for ${user.first_name} ${user.last_name || user.email}` : ""}`}
      formManager={formManager}
      maxWidth="lg"
      side="bottom"
      forceDrawer={false} // Let it decide automatically
    >
      <form onSubmit={handleSubmit} className="space-y-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-1">User</label>
            {hasPreselectedUser ? (
              <div className="flex items-center justify-between rounded border px-3 py-2 text-sm bg-muted/30">
                <span className="truncate">
                  {user && (user.first_name || user.last_name)
                    ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                    : (user?.email ?? "Selected user")}
                </span>
                <span className="text-xs text-muted-foreground">pre-selected</span>
              </div>
            ) : (
              <UserSelect
                currentUserId={pickedUserId}
                onUserSelect={async (id) => setPickedUserId(id)}
                showAllUsers={true}
              />
            )}
          </div>
          <div id="contact_id">
            <label className="block text-sm font-medium mb-1">Contact</label>
            <ClientSelectWithModal
              value={form.watch("contact_id") || ""}
              onValueChange={handleContactChange}
              items={clientOptions || []}
              disabled={optionsLoading || !!access}
              required
              error={!!form.formState.errors.contact_id}
              isSearching={isSearching}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search for a client..."
              highZIndex={true}
            />
            {form.formState.errors.contact_id && (
              <div className="text-xs text-red-600 mt-1 dark:text-red-400">
                {form.formState.errors.contact_id.message}
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-1">
            Access Level
            <AccessLevelInfoPopover />
          </label>
          <select
            id="access_level"
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.watch("access_level")}
            onChange={(e) => {
              // Narrow rather than assert: this value is written to the grant
              // row RLS reads, so an unrecognised option must not reach it.
              const level = toAccessLevel(e.target.value);
              if (!level) return;
              form.setValue("access_level", level, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            required
          >
            {accessLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
          {/* Inline helper text — the accurate meaning of the currently-selected
              level, always visible (not hidden behind the info icon). */}
          <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
            {accessLevelDescription(form.watch("access_level"))}
          </p>
          {form.formState.errors.access_level && (
            <div className="text-xs text-red-600 mt-1 dark:text-red-400">
              {form.formState.errors.access_level.message}
            </div>
          )}
        </div>

        {grantPreviewText && !access && (
          <GrantScopePreview accessLevel={selectedAccessLevel} text={grantPreviewText} />
        )}

        {effectiveUserId && (
          <div className="rounded border p-3">
            <div className="text-sm font-medium mb-3">Current client portal access</div>
            <ClientAccessGrantsSummary
              companyGrants={existingCompanyGrants ?? []}
              contactGrants={existingContactGrants ?? []}
              isLoading={existingLoading}
              showRevoke
              onRevokeCompany={handleRevokeCompany}
              onRevokeContact={handleRevokeContact}
              revokeCompanyPending={revokeCompanyMutation.isPending}
              revokeContactPending={revokeAccessMutation.isPending}
            />
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-gray-200 sticky bottom-0 bg-card dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={grantAccessMutation.isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={grantAccessMutation.isPending}
            className="w-full sm:w-auto"
          >
            {grantAccessMutation.isPending ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-gray-400 rounded-full"></span>
                {access ? "Updating..." : "Granting..."}
              </span>
            ) : access ? (
              "Update Access"
            ) : (
              "Grant Access"
            )}
          </Button>
        </div>
      </form>
    </FormModal>
  );
};
